import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useSupplierProductsImportListStore } from "@/stores/storage/SupplierProductsImportListStore"

/**
 * Suppliers Store
 *
 * Manages suppliers data and operations including:
 * - Fetching suppliers list with pagination
 * - Filtering suppliers by search query and visibility
 * - Managing loading states
 */
export const useSuppliersStore = defineStore('suppliersStore', () => {

  // ========================================
  // State
  // ========================================
  const importListStore = useSupplierProductsImportListStore()

  /** Global loading state for suppliers operations */
  const isLoading = ref(false)

  /** Loading state for saving operations */
  const isSaving = ref(false)

  /**
   * Main data object containing suppliers list and pagination info
   * @property {boolean} isLoaded - Whether the data has been fetched
   * @property {Array} suppliers - List of supplier objects
   * @property {Object} meta - Pagination metadata (total, perPage, page)
   */
  const suppliersData = ref({
    isLoaded: false,
    suppliers: [],
    meta: {
      total: 0,
      perPage: 10,
      page: 1,
    },
  })

  /** Search query for filtering suppliers */
  const searchQuery = ref('')

  /** Flag to show suppliers with mySupplier: true */
  const showOnlyYoursSuppliers = ref(true)

  // ========================================
  // Getters
  // ========================================

  /**
   * Computed property that filters suppliers based on search query and showOnlyYoursSuppliers flag
   * @returns {Array} Filtered list of suppliers
   */
  const filteredSuppliers = computed(() => {
    let result = suppliersData.value.suppliers

    // Filter by mySupplier if showOnlyYoursSuppliers is false
    if (showOnlyYoursSuppliers.value) {
      result = result.filter(supplier => supplier.mySupplier)
    }

    // Filter by search query
    if (searchQuery.value.trim()) {
      const query = searchQuery.value.toLowerCase().trim()
      result = result.filter(supplier =>
        supplier.name.toLowerCase().includes(query)
      )
    }

    return result
  })

  // ========================================
  // Actions
  // ========================================

  /**
   * Fetch suppliers list from the backend
   *
   * Retrieves paginated list of suppliers with their properties:
   * - id, name
   * - showImportButton, showEditButton, showProductsButton
   * - mySupplier
   *
   * @param {number} page - Page number to fetch (default: 1)
   * @param {number} perPage - Number of items per page (default: 100)
   * @param {boolean} force - If true, refetch data even if already loaded
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  const fetchSuppliers = async (page = 1, perPage = 200, force = false) => {
    // Skip fetching if data is already loaded and force is not set
    if (!force && suppliersData.value.isLoaded && suppliersData.value.meta.page === page) {
      return true
    }

    isLoading.value = true

    try {
      const response = await $backend(`/salonerSuppliers/index?page=${page}&perPage=${perPage}`, {
        method: 'GET',
      })

      if (response.success) {
        suppliersData.value.isLoaded = true
        suppliersData.value.suppliers = response.data.salonerSuppliers || []
        suppliersData.value.meta = response.meta || {
          total: 0,
          perPage: perPage,
          page: page,
        }

        if(suppliersData.value.suppliers.filter(s => s.mySupplier).length === 0) showOnlyYoursSuppliers.value = false

        importListStore.importListCounter = response.data.listCounter || 0

        return true
      }
    } catch (error) {
      //console.error('Error fetching suppliers:', error)

      return false
    } finally {
      isLoading.value = false
    }

    return false
  }

  /**
   * Change the current page and refetch suppliers
   *
   * @param {number} page - New page number
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  const changePage = async (page) => {
    return await fetchSuppliers(page, suppliersData.value.meta.perPage, true)
  }

  /**
   * Toggle showOnlyYoursSuppliers flag and reset to page 1
   *
   * When toggling the filter, pagination is reset to first page
   * to ensure consistent user experience
   *
   * @returns {Promise<void>}
   */
  const toggleShowAll = async () => {
    showOnlyYoursSuppliers.value = !showOnlyYoursSuppliers.value
    // Reset to page 1 when toggling filter
    //await changePage(1)
  }

  /**
   * Set the search query for filtering suppliers
   *
   * @param {string} query - Search query string
   */
  const setSearchQuery = (query) => {
    searchQuery.value = query
  }

  /**
   * Clear all filters and reset to initial state
   */
  const clearFilters = () => {
    searchQuery.value = ''
    showOnlyYoursSuppliers.value = false
  }

  /**
   * Refresh suppliers data by forcing a refetch
   *
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  const refreshSuppliers = async () => {
    return await fetchSuppliers(
      suppliersData.value.meta.page,
      suppliersData.value.meta.perPage,
      true,
    )
  }

  /**
   * Save supplier (create new or update existing)
   *
   * @param {Object} supplierData - Supplier data to save
   * @param {number} [supplierData.id] - Supplier ID (only for update)
   * @param {string} supplierData.name - Supplier name
   * @returns {Promise<{success: boolean, message: string}>} Result object
   */
  const saveSupplier = async (supplierData) => {
    isSaving.value = true

    try {
      const body = supplierData.id
        ? { id: supplierData.id, name: supplierData.name }
        : { name: supplierData.name }

      const response = await $backend('/salonerSuppliers/save', {
        method: 'POST',
        body,
      })

      return {
        success: response.success || false,
        message: response.message || '',
        data: response.data || null,
      }
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Wystąpił błąd podczas zapisywania dostawcy',
        data: null,
      }
    } finally {
      isSaving.value = false
    }
  }

  /**
   * Delete supplier by ID
   *
   * @param {number} supplierId - ID of supplier to delete
   * @returns {Promise<{success: boolean, message: string}>} Result object
   */
  const deleteSupplier = async (supplierId) => {
    isSaving.value = true

    try {
      const response = await $backend('/salonerSuppliers/delete', {
        method: 'POST',
        body: { id: supplierId },
      })

      return {
        success: response.success || false,
        message: response.message || '',
      }
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Wystąpił błąd podczas usuwania dostawcy',
      }
    } finally {
      isSaving.value = false
    }
  }

  // ========================================
  // Exports
  // ========================================

  return {
    // State
    isLoading,
    isSaving,
    suppliersData,
    searchQuery,
    showOnlyYoursSuppliers,

    // Getters
    filteredSuppliers,

    // Actions
    fetchSuppliers,
    changePage,
    toggleShowAll,
    setSearchQuery,
    clearFilters,
    refreshSuppliers,
    saveSupplier,
    deleteSupplier,
  }
})
